/**
 * Links list for HTML messages. The message iframe is sandboxed so links in it are inert; this lists them
 * where the reader can see the real destination. The already-sanitised html string is parsed with
 * DOMParser (an inert document) and is never inserted into the live DOM.
 */
export interface MailLink {
  href: string;
  /** Visible anchor text (whitespace collapsed); may be empty. */
  text: string;
  /** Hostname for http(s); the address domain for mailto. */
  host: string;
  kind: 'web' | 'mailto';
  /** The visible text names a different domain than the link actually goes to. */
  mismatch: boolean;
  /** Host uses an internationalised (punycode) label, a common look-alike trick. */
  punycode: boolean;
}

const DOMAIN_IN_TEXT = /(?:^|[^a-z0-9.-])((?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,})(?![a-z0-9-])/i;

const bare = (host: string) => host.toLowerCase().replace(/^www\./, '');

/** Domain the visible text claims to point to, if it looks like a URL, host name or email address. */
function claimedDomain(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const fromEmail = /^[^\s@]+@([^\s@]+\.[a-z]{2,})$/i.exec(trimmed);
  if (fromEmail) return bare(fromEmail[1]);
  try {
    if (/^https?:\/\//i.test(trimmed)) return bare(new URL(trimmed).hostname);
  } catch {
    /* fall through to the loose match */
  }
  const m = DOMAIN_IN_TEXT.exec(trimmed);
  return m ? bare(m[1]) : null;
}

function sameSite(a: string, b: string): boolean {
  return a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`);
}

export function extractMailLinks(html: string | undefined): MailLink[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const seen = new Map<string, number>();
  const links: MailLink[] = [];
  doc.querySelectorAll('a[href]').forEach((a) => {
    const raw = (a.getAttribute('href') ?? '').trim();
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return; // relative or malformed: nothing to show
    }
    const protocol = url.protocol.toLowerCase();
    let host: string;
    let kind: MailLink['kind'];
    if (protocol === 'http:' || protocol === 'https:') {
      host = url.hostname;
      kind = 'web';
    } else if (protocol === 'mailto:') {
      const addr = decodeURIComponent(url.pathname).split(',')[0] ?? '';
      host = addr.includes('@') ? addr.slice(addr.lastIndexOf('@') + 1) : '';
      kind = 'mailto';
    } else {
      return;
    }
    if (!host) return;
    const href = url.href;
    const text = (a.textContent ?? '').replace(/\s+/g, ' ').trim();
    const claimed = claimedDomain(text);
    const entry: MailLink = {
      href,
      text,
      host,
      kind,
      mismatch: !!claimed && !sameSite(claimed, bare(host)),
      punycode: host.toLowerCase().split('.').some((l) => l.startsWith('xn--')),
    };
    const at = seen.get(href);
    if (at === undefined) {
      seen.set(href, links.length);
      links.push(entry);
    } else if (entry.mismatch && !links[at].mismatch) {
      links[at] = entry; // the same destination under deceptive text must not be hidden by a benign twin
    }
  });
  return links;
}
